import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '../../components/CardShell';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useTableSort } from '../../hooks/useTableSort';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import { getPlRevenueView } from '../../api/financials_pl';
import { listOffers, createOffer, updateOffer, deleteOffer, importOffersFromCards,
         listOfferModels } from '../../api/salesOffers';
import { Cell, Choix } from '../../components/InlineCells';
import { MODELS as MODELS_ALL, OfferIcon, iconOf, libelleModel } from './offerIcons';
import OfferCreateDialog, { PRINCIPAUX } from './OfferCreateDialog';
import { TIME_UNITS, libelleUnite, cleUnite, sansInducteur } from './offerUnits';
import './OffersPage.css';

/* ── Offre ─────────────────────────────────────────────────────────────────
   Le catalogue de ce que l'entreprise vend : une offre = un modèle de vente
   et ses règles (unité, tarif de référence, coût, compte de CA, délai de
   paiement, étalement du CA). Les cards du plan des ventes en sont des usages.
   Les modèles sont ceux des cards, pour parler la même langue.

   Deux onglets Produits · Services (2026-09-08) : un seul catalogue, un seul
   stockage, la nature ne fait que RÉDUIRE ce qu'on voit, offres comme modèles.
   Ils servent le parcours de bout en bout, produits d'abord. */

/* Les six équations de CA (décision 2026-09-04). L'équation dit le CA ; la
   facturation et l'étalement restent des attributs à part. */

const libelleEquation = (p, e) => ({
  volume_price: p('Volume × prix'), time_rate: p('Temps × tarif'),
  capacity_occupancy: p('Capacité × occupation'), base_arpu: p('Base × revenu unitaire'),
  base_rate: p('Assiette × taux'), project: p('Montant planifié'),
}[e] || e);
/* Les trois natures, déclarées UNE fois : les onglets et l'écran de sélection
   lisent la même liste, dans le même ordre. */
const NATURES = [{ id: 'product', label: 'Marchandises' },
                 { id: 'labour', label: 'Prestations' },
                 { id: 'service', label: 'Droits & usages' }];
/* Les modèles qu'une offre peut prendre : ceux que la fenêtre propose, dans
   son ordre, et le modèle COURANT s'il n'en fait pas partie (2026-09-12 : le
   catalogue n'a plus de coche, il se lit). */
const MODELES_TOUS = (courant = null) => {
  const ids = PRINCIPAUX.includes(courant) || !courant ? PRINCIPAUX : [...PRINCIPAUX, courant];
  return ids.map((id) => MODELS_ALL.find((m) => m.id === id)).filter(Boolean);
};
/* Ce qu'on planifie avec chaque modèle proposé, en une phrase (2026-09-12). */
const PHRASES = (p) => ({
  produit: p('Des quantités vendues à un prix unitaire'),
  conseil: p('Des heures ou des jours-hommes à un tarif'),
  carnet: p('Des carnets vendus, consommés sur plusieurs mois'),
  services_manages: p('Des contrats facturés à chaque période'),
  projet: p('Un montant global, reconnu sur la durée'),
  abonnement: p('Des abonnés à un revenu unitaire'),
  formation: p('Des participants ou des sessions à un prix'),
  espace: p("Une capacité, un taux d'occupation, un tarif"),
});
const famDe = (model) => (MODELS_ALL.find((m) => m.id === model) || {}).fam || 'service';
/* UNE SEULE VUE (2026-09-10) : tout ce qu'une offre porte tient sur sa ligne,
   depuis que le compte, le prix et le coût vivent ailleurs. Chaque colonne est
   taillée sur son contenu le plus long, entête comprise. */
/* La TABLE des offres (2026-09-11) : on y règle ce qui ne change pas le modèle,
   le nom, le prix, l'unité, la facturation, l'étalement, le paiement. L'équation
   se lit. Le modèle, les quantités et la capacité se règlent dans la fenêtre. */
const VUES = {
  tout: { lbl: 'Offres', cols: [['equation', 'Équation', '', 150], ['price', 'Prix', 'lk-num', 90],
                                ['unit', 'Unité', '', 100], ['accounting', 'Comptabilisation', '', 220],
                                /* LA TVA DE L'OFFRE (2026-09-27) : le taux normal du client ou sans
                                   TVA ; le taux lui-même vit dans Paramètres › Finance. */
                                ['vat', 'TVA', '', 150],
                                ['billing', 'Conditions', '', 280], ['payment', 'Encaissement', '', 140],
                                ['active', 'Actif', '', 60]] },
};
const libelleMode = (p, m) => ({ one_time: p('Ponctuel'), recurring: p('Récurrent'), usage: p("À l'usage") }[m] || '–');
const libelleFacture = (p, b) => ({
  monthly: p('mensuelle'), periodic: p('tous les…'), on_sale: p('à la commande'), on_due: p('au début de chaque période'), on_delivery: p('à la livraison'),
}[b] || b);
/* Le délai de paiement se compte en MOIS, 0 à 6 (2026-09-08) : « selon le DSO » renvoyait
   à un driver global de Finance que rien ne reliait à l'offre. */
const libelleDelai = (p, n) => (n === 0 ? p('le mois de la facture') : n === 1 ? p('le mois suivant') : `${n} ${p('mois après')}`);
const libelleMoment = (p, a) => ({ order: p('à la commande'), delivery: p('à la livraison'), m: p('mois après la commande') }[a] || a);
/* Un moment de tranche se code order · delivery · m<n> (n mois après la commande). */
const momentDe = (at) => (at && at.startsWith('m') ? 'm' : at || 'order');
const moisDe = (at) => (at && at.startsWith('m') ? Number(at.slice(1)) : null);
const num = (v, d = 0) => (v == null || v === '' ? '' : formatNumber(v, d));

/* Les CONDITIONS se lisent (2026-09-11) : facturation et paiement se règlent
   dans la fenêtre de l'offre, qui n'offre que ce qui a un sens pour le modèle ;
   la table les affiche, elle ne les contredit pas. */
const texteTranche = (p, t) => `${num(t.pct, 0)} % ${momentDe(t.at) === 'm' ? `${moisDe(t.at)} ${p('mois après la commande')}` : libelleMoment(p, momentDe(t.at))}`;
const textePaiement = (p, o) => (o.payment_months == null ? '–' : libelleDelai(p, o.payment_months));
const texteFacture = (p, o) => {
  if (o.billing === 'periodic') return `${p('à la commande, tous les')} ${num(o.billing_months || 1)} ${p('mois')}`;
  if (o.billing === 'tranches') return `${p('par tranches')} · ${(o.payment_tranches || []).map((t) => texteTranche(p, t)).join(' · ')}${o.lead_months ? ` · ${p('commande')} ${num(o.lead_months)} ${p('mois avant la livraison')}` : ''}`;
  if (o.billing === 'on_sale' && o.lead_months) return `${p('à la commande')} · ${num(o.lead_months)} ${p('mois avant la livraison')}`;
  if (o.billing === 'on_delivery' && o.plan_basis === 'order' && o.lead_months) return `${p('à la livraison')} · ${num(o.lead_months)} ${p('mois après la commande')}`;
  return libelleFacture(p, o.billing);
};

function Th({ col, label, cls = '', sort, style, onResize }) {
  const actif = sort.sortCol === col;
  const ref = React.useRef(null);
  /* La poignée à droite de l'entête : on tire, la colonne suit. Le clic de tri
     ne part pas d'une poignée. */
  const tirer = (e) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX; const w0 = ref.current.getBoundingClientRect().width;
    const bouge = (ev) => onResize(w0 + ev.clientX - x0);
    const lache = () => { window.removeEventListener('mousemove', bouge); window.removeEventListener('mouseup', lache); };
    window.addEventListener('mousemove', bouge); window.addEventListener('mouseup', lache);
  };
  return (
    <th ref={ref} className={`${cls} of-th`} style={style} onClick={() => sort.handleSort(col)}
        aria-sort={actif ? (sort.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      {label}{actif ? (sort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
      {onResize && <span className="of-th__grip" onMouseDown={tirer} onClick={(e) => e.stopPropagation()} aria-hidden="true" />}
    </th>
  );
}

/* `only` (2026-09-10) : « models », le catalogue des modèles ; « offers », la
   table des offres. Les deux se montent SANS gabarit de page, en onglets de la
   Trajectoire : on règle ce qu'on vend là où on le planifie. La section Offre
   du menu a disparu le même jour. */
export default function OffersPage({ activeTenant, year, addToast, canWrite = false, only = 'models' }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [offers, setOffers] = useState(null);
  const [lines, setLines] = useState([]);
  const sort = useTableSort(`of_sort_${tid || 'none'}`, 'display_order');
  /* Le catalogue des modèles se trie aussi (2026-09-11), par défaut dans
     l'ordre du catalogue ; un tri par colonne traverse les natures. */
  const [drag, setDrag] = useState(null);          // offre saisie par sa poignée
  const [creation, setCreation] = useState(false);  // la fenêtre « Nouvelle offre »
  const [edition, setEdition] = useState(null);     // l'offre ouverte en modification dans la fenêtre
  const vueOffres = 'tout';
  /* Les largeurs se règlent à la souris sur l'entête (2026-09-11) et se
     retiennent par client dans le navigateur. */
  const [largeurs, setLargeurs] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem(`of_cols_${tid || 'none'}`) || '{}'); } catch (e) { return {}; }
  });
  const poserLargeur = (id, w) => {
    const n = { ...largeurs, [id]: Math.max(60, Math.round(w)) };
    setLargeurs(n);
    try { window.localStorage.setItem(`of_cols_${tid || 'none'}`, JSON.stringify(n)); } catch (e) { /* quota */ }
  };
  const largeurDe = (id, w) => largeurs[id] || w;
  const [vuePar, setVuePar] = useState(() => {
    try { const v = window.localStorage.getItem(`of_vue_${tid || 'none'}`); return ['offre', 'compte', 'nature'].includes(v) ? v : 'nature'; } catch (e) { return 'nature'; }
  });
  const poserVue = (v) => { setVuePar(v); try { window.localStorage.setItem(`of_vue_${tid || 'none'}`, v); } catch (e) { /* quota */ } };
  const [modelesCat, setModelesCat] = useState(null); // les modèles du catalogue, avec les défauts du client
  const dragOn = sort.sortCol === 'display_order';

  const reload = useCallback(async () => {
    if (!tid) return;
    const r = await listOffers(tid);
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    setOffers(r.data);
  }, [tid, addToast]);
  useEffect(() => { reload(); }, [reload]);
  /* La sélection du client réduit ce qu'on propose ; tant qu'elle n'est pas
     chargée, on ne masque rien. */
  const rechargerModeles = useCallback(async () => {
    if (!tid) return;
    const r = await listOfferModels(tid);
    if (!r.error) setModelesCat(r.data);
  }, [tid]);
  useEffect(() => { rechargerModeles(); }, [rechargerModeles]);
  /* Les comptes de CA du compte de résultat : le rattachement de chaque offre, et
     l'assiette d'une commission. Ventes se vend SEUL (décision 2026-09-08) : sans
     Finance, il n'y a aucune ligne, le compte devient facultatif et rien ne s'affiche
     en attente. Le module reste connectable, le jour où Finance arrive. */
  useEffect(() => {
    let vivant = true;
    if (!tid || !year) return undefined;
    getPlRevenueView(tid, year).then((r) => {
      if (!vivant) return;
      const rows = r?.data?.rows || [];
      const seen = new Set();
      setLines(rows.filter((x) => x.type === 'detail' && x.section === 'revenue' && !seen.has(x.label) && seen.add(x.label))
        .map((x) => ({ value: x.code, label: x.label })));
    });
    return () => { vivant = false; };
  }, [tid, year]);

  /* Aucun export ici : la Trajectoire, qui héberge ces tables, porte le sien. */

  const onPatch = async (id, patch) => {
    const r = await updateOffer(tid, id, patch);
    if (r.error) addToast && addToast(r.error, 'error');
    await Promise.all([reload(), rechargerModeles()]);
  };
  const onDelete = async (id) => {
    const r = await deleteOffer(tid, id);
    if (r.error) addToast && addToast(r.error, 'error'); else addToast && addToast(p('Offre supprimée'));
    await reload();
  };
  /* « ＋ Offre » ouvre la fenêtre (2026-09-11) : on choisit COMMENT on vend,
     les conditions du catalogue se posent, on ajuste, on crée. Une offre ne
     naît plus muette sur le premier modèle coché. */
  const onAdd = () => setCreation(true);
  /* Une offre enregistrée apprend à son modèle ses défauts (compte, unité,
     décimales) : on relit le catalogue avec les offres, la prochaine fenêtre
     arrive pré-remplie (2026-09-12). */
  const onCreer = async (champs) => {
    const kind = { product: 'units', service: 'recurring', labour: 'presta' }[famDe(champs.model)];
    const r = await createOffer(tid, { kind, ...champs, display_order: (offers || []).length });
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    setCreation(false);
    await Promise.all([reload(), rechargerModeles()]);
  };
  /* Le crayon d'une ligne rouvre la même fenêtre sur l'offre : on relit ses
     conditions en phrases avant de les changer. */
  const onModifier = async (champs) => {
    const r = await updateOffer(tid, edition.id, champs);
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    setEdition(null);
    await Promise.all([reload(), rechargerModeles()]);
  };
  /* Glisser-déposer simple : on dépose une offre sur une autre de la même
     famille, elle prend sa place ; l'ordre est renuméroté et enregistré. */
  const onDrop = async (targetId) => {
    if (!drag || drag === targetId || !canWrite) { setDrag(null); return; }
    const src = tous.find((o) => o.id === drag); const dst = tous.find((o) => o.id === targetId);
    if (!src || !dst) { setDrag(null); return; }
    const fam = [...tous].sort((a, b) => a.display_order - b.display_order);
    const list = fam.filter((o) => o.id !== src.id);
    list.splice(list.findIndex((o) => o.id === dst.id) + (fam.indexOf(src) < fam.indexOf(dst) ? 1 : 0), 0, src);
    setDrag(null);
    setOffers((prev) => prev.map((o) => { const i = list.findIndex((x) => x.id === o.id); return i < 0 ? o : { ...o, display_order: i }; }));
    for (let i = 0; i < list.length; i++) {
      if (list[i].display_order !== i) await updateOffer(tid, list[i].id, { display_order: i });
    }
    await reload();
  };
  const onImport = async () => {
    const r = await importOffersFromCards(tid);
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    addToast && addToast(`${r.data.created} ${p('offre(s) reprise(s) des cards')}`);
    await reload();
  };

  /* UN SEUL catalogue (2026-09-10) : les trois natures ne se filtrent plus, elles
     GROUPENT la liste. Trois onglets pour trois rangements faisaient chercher une
     offre avant de la lire, et un client qui ne coche que trois modèles n'avait
     plus rien à ranger. */
  const tous = offers || [];
  const garder = () => true;
  /* Toute colonne se trie (2026-09-11) : une valeur lisible pour le texte, un
     rang pour ce qui se compare. */
  const valeur = (o, col) => (col === 'equation' ? libelleEquation(p, o.equation) : col === 'unit' ? libelleUnite(p, o.unit)
    : col === 'model' ? libelleModel(p, o.model || 'autre')
    : col === 'billing' ? libelleFacture(p, o.billing)
    : col === 'recognition' ? (o.recognition === 'spread' ? (o.period_months || 1) : 1)
    : col === 'payment' ? (o.payment_months ?? 99)
    : col === 'tranches' ? (o.payment_tranches || []).length
    : col === 'decimals' ? (o.decimals ? 1 : 0) : col === 'capacity' ? (o.capacity || 0)
    : col === 'active' ? (o.active ? 0 : 1)
    : col === 'vat' ? (o.vat_kind === 'none' ? 1 : 0)
    : col === 'revenue_line_id' ? (lines.find((l) => l.value === o.revenue_line_id) || {}).label || '' : o[col]);
  const visibles = sort.sortRows(tous.filter(garder), valeur);

  /* Plus aucun filtre (2026-09-10) : un catalogue tient sur un écran, on le lit. */
  /* La reprise des cards est un PONT de transition (2026-09-09) : elle ramène en
     offres le plan des ventes d'avant. Elle ne s'offre donc que sur un catalogue
     encore vide ; dès qu'une offre existe, le catalogue est la source. */
  const reprise = canWrite && offers && !offers.length ? (
    <button type="button" className="of-btn" onClick={onImport}>{p('Reprendre les cards')}</button>
  ) : null;

  /* L'inducteur : heures ou jours-homme pour le temps ; un texte libre ailleurs (les unités
     connues se proposent par leur libellé) ; rien pour une commission ou un projet. */
  const inducteur = (o) => (
    sansInducteur(o)
      ? <span className="of-muted">{o.equation === 'base_rate' ? '%' : p('valeur')}</span>
      : o.equation === 'time_rate'
        ? <Choix value={TIME_UNITS.includes(o.unit) ? o.unit : 'h'} canWrite={canWrite} onSave={(v) => onPatch(o.id, { unit: v })}
                 options={TIME_UNITS.map((u) => ({ value: u, label: libelleUnite(p, u) }))} />
        : <Cell value={o.unit} canWrite={canWrite} format={(v) => libelleUnite(p, v)} toDraft={(v) => libelleUnite(p, v)}
                placeholder="…" onSave={(v) => onPatch(o.id, { unit: cleUnite(p, v) })} />
  );
  /* La LIGNE des offres (2026-09-10) : rien à ouvrir pour régler, mais pas
     treize colonnes non plus. Quatre VUES se partagent les cellules, la
     colonne Offre restant en tête de chacune : Généralités · Prix & marges ·
     Conditions · Autres paramètres. */
  const tauxClient = Number(activeTenant?.vat_rate);
  const tvaOptions = [
    { value: 'normal', label: Number.isFinite(tauxClient) ? `${p('Taux normal')} (${num(tauxClient * 100, 1)} %)` : p('Taux normal') },
    { value: 'none', label: p('Sans TVA') },
  ];
  const cellules = (o) => {
    const libre = o.model === 'autre';
    const produitFige = (o.family === 'product' && !libre) || o.model === 'formation';   // reconnaissance à la livraison, sans étalement
    const prixSub = { base_arpu: `/ ${p('mois')}` }[o.equation] || null;
    return {
      model: <td key="model"><Choix value={o.model || 'autre'} canWrite={canWrite} onSave={(v) => onPatch(o.id, { model: v })}
                                    options={MODELES_TOUS(o.model).map((m) => ({ value: m.id, label: libelleModel(p, m.id) }))} /></td>,
      equation: <td key="equation" className="of-mini"><span className="of-txt of-ro">{libelleEquation(p, o.equation)}</span></td>,
      revenue_line_id: (
        <td key="revenue_line_id"><Choix value={o.revenue_line_id} canWrite={canWrite} onSave={(v) => onPatch(o.id, { revenue_line_id: v })}
                                         options={lines} emptyLabel={lines.length ? p('à attribuer') : p('sans compte')}
                                         tone={o.revenue_line_id || !lines.length ? '' : 'lk-todo'} /></td>),
      unit: <td key="unit">{inducteur(o)}</td>,
      vat: (
        <td key="vat" className="of-mini"><Choix value={o.vat_kind || 'normal'} canWrite={canWrite} onSave={(v) => onPatch(o.id, { vat_kind: v })}
                                               options={tvaOptions} tone={o.vat_kind === 'none' ? 'of-novat' : ''} /></td>),
      price: (
        <td key="price" className="lk-num">
          {o.equation === 'project' ? <span className="of-na">{p('n/a')}</span>
            : o.equation === 'base_rate'
            ? <Cell value={o.rate_pct} type="number" canWrite={canWrite} format={(v) => (v == null ? '' : `${num(v, 2)} %`)} placeholder="…" onSave={(v) => onPatch(o.id, { rate_pct: v })} />
            : <span className="of-prix"><Cell value={o.price} type="number" canWrite={canWrite} format={(v) => num(v)} placeholder="–" onSave={(v) => onPatch(o.id, { price: v })} />
                {prixSub && <span className="of-suffix">{prixSub}</span>}</span>}
        </td>),
      billing: (
        <td key="billing" className="of-mini"><span className="of-txt of-ro">{texteFacture(p, o)}</span></td>),
      /* COMPTABILISATION (2026-09-11) : le compte de revenu et le mode de
         reconnaissance, sur deux rangs dans la cellule. */
      accounting: (
        <td key="accounting" className="of-mini of-cell-multi">
          <Choix value={o.revenue_line_id} canWrite={canWrite} onSave={(v) => onPatch(o.id, { revenue_line_id: v })}
                 options={lines} emptyLabel={lines.length ? p('à attribuer') : p('sans compte')}
                 tone={o.revenue_line_id || !lines.length ? '' : 'lk-todo'} />
          <span className="of-acc">
            {produitFige ? <span className="of-txt of-ro">{p('à la livraison')}</span>
              : <>
                  <Choix value={o.recognition === 'spread' ? 'spread' : 'same'} canWrite={canWrite}
                         onSave={(v) => onPatch(o.id, v === 'spread' ? { recognition: 'spread', period_months: Math.max(2, o.period_months || 2) } : { recognition: 'same_month', period_months: null })}
                         options={[{ value: 'same', label: p('à la livraison') }, { value: 'spread', label: p('sur plusieurs mois') }]} />
                  {o.recognition === 'spread' && <><Cell value={o.period_months || 2} type="number" canWrite={canWrite} format={(v) => (v == null ? '' : num(v))} placeholder="…"
                        onSave={(v) => onPatch(o.id, { recognition: 'spread', period_months: Math.max(2, Math.round(Number(v) || 2)) })} /><span className="of-muted"> {p('mois')}</span></>}
                </>}
          </span>
        </td>),
      recognition: (
        <td key="recognition" className="lk-num of-mini">
          {produitFige ? <span className="of-txt of-ro">1</span>
            : <Cell value={o.recognition === 'spread' ? (o.period_months || 1) : 1} type="number" canWrite={canWrite}
                    format={(v) => (v == null ? '' : num(v))} placeholder="1"
                    onSave={(v) => { const n = Math.max(1, Math.round(Number(v) || 1));
                      onPatch(o.id, n > 1 ? { recognition: 'spread', period_months: n }
                                          : { recognition: 'same_month', period_months: null }); }} />}
        </td>),
      payment: (
        <td key="payment" className="of-mini"><span className="of-txt of-ro">{textePaiement(p, o)}</span></td>),
      tranches: (
        <td key="tranches" className="of-mini of-cell-multi">
          {o.billing === 'tranches'
            ? <span className="of-txt of-ro">{(o.payment_tranches || []).map((t) => texteTranche(p, t)).join(' · ')}</span>
            : <span className="of-muted">–</span>}
        </td>),
      decimals: (
        <td key="decimals" className="of-mini">
          {produitFige ? <span className="of-txt of-ro">{p('entières')}</span>
            : <Choix value={o.decimals ? 'dec' : 'ent'} canWrite={canWrite} onSave={(v) => onPatch(o.id, { decimals: v === 'dec' })}
                     options={[{ value: 'ent', label: p('entières') }, { value: 'dec', label: p('décimales') }]} />}
        </td>),
      capacity: (
        <td key="capacity" className="lk-num of-mini">
          {o.equation === 'capacity_occupancy'
            ? <><Cell value={o.capacity} type="number" canWrite={canWrite} format={(v) => num(v)} placeholder="…" onSave={(v) => onPatch(o.id, { capacity: v })} /> <span className="of-muted">{libelleUnite(p, o.unit)}</span></>
            : <span className="of-muted">–</span>}
        </td>),
      active: (
        <td key="active" className="of-mini"><Choix value={o.active ? 'oui' : 'non'} canWrite={canWrite}
                                                     onSave={(v) => onPatch(o.id, { active: v === 'oui' })}
                                                     options={[{ value: 'oui', label: p('oui') }, { value: 'non', label: p('non') }]} /></td>),
    };
  };
  /* Plus de colonne figée (2026-09-11) : la table s'est assez raccourcie pour
     tenir, et les largeurs se règlent à la souris sur l'entête. */
  const figee = () => ({});
  const ligne = (o) => {
    const c = cellules(o);
    const cols = VUES[vueOffres].cols.map(([id], k) => {
      const f = figee(k + 1);
      return f.className ? React.cloneElement(c[id], { className: `${c[id].props.className || ''} ${f.className}`.trim(), style: f.style }) : c[id];
    });
    return (
      <tr key={o.id} className={`of-row${drag === o.id ? ' is-dragging' : ''}`}
          onDragOver={(e) => { if (dragOn && drag) e.preventDefault(); }} onDrop={() => onDrop(o.id)}>
        <td className="of-lbl lk-gutter">
          {canWrite && dragOn && (
            <span className="lk-grip" draggable title={p("Glisser pour réordonner")}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDrag(o.id); }} onDragEnd={() => setDrag(null)}>⋮⋮</span>
          )}
          <span className="of-lblwrap">
            <span className="of-icobtn"><OfferIcon name={iconOf(o)} /></span>
            <Cell value={o.name} canWrite={canWrite} onSave={(v) => onPatch(o.id, { name: v })} />
            {/* Le crayon suit le nom (2026-09-11) : c'est l'offre qu'on modifie, il se
                tient à côté d'elle, pas en bout d'une ligne de dix colonnes. */}
            {canWrite && <button type="button" className="lk-edit of-edit" title={p("Modifier l'offre")} onClick={(e) => { e.stopPropagation(); setEdition(o); }}>✎</button>}
          </span>
        </td>
        {cols}
        <td className="lk-actions">
          {canWrite && <button type="button" className="lk-x lk-del" title={p("Supprimer l'offre")} onClick={(e) => { e.stopPropagation(); onDelete(o.id); }}>✕</button>}
        </td>
      </tr>
    );
  };
  /* Le CATALOGUE (2026-09-12) : une page d'INFORMATION, rien ne s'y règle.
     Deux cartes : les modèles que la fenêtre d'offre propose, en tuiles et
     dans son ordre, chacune disant ce qu'on y planifie ; puis, en pastilles,
     ceux qu'on ne fait que sur demande, avec l'invitation à nous écrire. Le
     compte, l'inducteur et les quantités se règlent sur l'offre. */
  const catalogue = () => {
    const parCle = Object.fromEntries((modelesCat || []).map((m) => [m.model, m]));
    const proposes = PRINCIPAUX.map((k) => parCle[k]).filter(Boolean);
    const autres = MODELS_ALL.filter((x) => !PRINCIPAUX.includes(x.id)).map((x) => parCle[x.id]).filter(Boolean);
    const phrases = PHRASES(p);
    return (
      <>
        <CardShell className="of-card" title={p('Les modèles existants')}>
          <div className="of-tiles">
            {proposes.map((m) => (
              <div key={m.model} className="of-tile">
                <div className="of-tile__nm"><OfferIcon name={iconOf({ model: m.model })} /> {libelleModel(p, m.model)}</div>
                <div className="of-tile__ph">{phrases[m.model]}</div>
                <div className="of-tile__tg">
                  {m.mode && <span>{libelleMode(p, m.mode)}</span>}
                  {m.equation && <span>{libelleEquation(p, m.equation)}</span>}
                </div>
              </div>
            ))}
          </div>
        </CardShell>
        <CardShell className="of-card" title={p('Modèles sur demande')}>
          <div className="of-dem">
            <div className="of-chips">
              {autres.map((m) => (
                <span key={m.model} className="of-chip"><OfferIcon name={iconOf({ model: m.model })} /> {libelleModel(p, m.model)}</span>
              ))}
            </div>
            <div className="of-ask">
              <div className="of-ask__t">{p('Un modèle vous manque ?')}</div>
              <div className="of-ask__s">{p("Décrivez votre offre en quelques lignes : nous l'ajoutons à Palestre et vous prévenons dès qu'elle est disponible.")}</div>
              <a className="of-cta" href={`mailto:olivier@myfp.ch?subject=${encodeURIComponent(p("Palestre : demande d'un nouveau modèle d'offre"))}`}>
                {p('Demander un modèle')}
              </a>
            </div>
          </div>
        </CardShell>
      </>
    );
  };

  if (!tid) return <p className="of-muted">{p('Choisir un client.')}</p>;
  if (only === 'models') return catalogue();
  /* Les OFFRES, une ligne par produit, groupées par nature ; quatre vues se
     partagent les colonnes. La table est à la largeur de ses colonnes. */
  /* L'ordre du catalogue groupe par nature ; un tri par colonne fait une seule
     liste, et un bouton ramène la vue standard. */
  /* VUE PAR (2026-09-12) : une seule liste (Offre), les comptes de chiffre
     d'affaires (Compte, « Sans compte » en dernier) ou les natures. Un tri par
     colonne fait une seule liste ; « Vue groupée » y ramène. */
  const trieOffres = sort.sortCol !== 'display_order';
  const parCompte = () => {
    const ids = [...lines.map((l) => l.value), null];
    const sans = visibles.filter((o) => o.revenue_line_id && !lines.some((l) => l.value === o.revenue_line_id));
    return [
      ...ids.map((id) => ({ id: id || 'sans', label: id ? (lines.find((l) => l.value === id) || {}).label : 'Sans compte',
                            items: visibles.filter((o) => (id ? o.revenue_line_id === id : !o.revenue_line_id || sans.includes(o))) })),
    ].filter((g) => g.items.length);
  };
  const groupes = trieOffres || vuePar === 'offre'
    ? [{ id: 'tous', label: null, items: visibles }]
    : vuePar === 'compte' ? parCompte()
      : NATURES.map((n) => ({ ...n, items: visibles.filter((o) => (o.family || 'service') === n.id) })).filter((n) => n.items.length);
  const retourOffres = trieOffres && vuePar !== 'offre' && (
    <button type="button" className="of-btn" onClick={() => { sort.setSortCol('display_order'); sort.setSortDir('asc'); }}>
      {p('Vue groupée')}
    </button>
  );
  const basculeVue = (
    <SegmentedToggle label={p('Vue par')} value={vuePar} onChange={poserVue}
                     options={[{ value: 'offre', label: p('Offre') }, { value: 'compte', label: p('Compte') }, { value: 'nature', label: p('Nature') }]} />
  );
  return (
    <>
      {creation && <OfferCreateDialog modeles={modelesCat} lines={lines} onCreate={onCreer} onClose={() => setCreation(false)} />}
      {edition && <OfferCreateDialog modeles={modelesCat} lines={lines} offre={edition} onCreate={onModifier} onClose={() => setEdition(null)} />}
        <CardShell className="of-card" title={p('Offres')} viewControls={basculeVue}
                   primaryAction={<>{retourOffres}{reprise}{canWrite && <button type="button" className="of-btn" data-tour="offers-add" onClick={onAdd}>＋ {p('Offre')}</button>}</>}
                   flush>
          {offers && tous.length === 0 ? (
            <div className="of-empty">
              <p>{p('Aucune offre pour ce client.')}</p>
              {canWrite && <button type="button" className="of-btn" onClick={onAdd}>＋ {p('Offre')}</button>}
            </div>
          ) : (
            <div className="of-scroll lk-hscroll">
              {/* La table tient dans la carte (2026-09-11) : sept colonnes, pas de
                  défilement ; les largeurs restent réglables à la souris. Tant
                  qu'aucune n'a été réglée, chaque colonne garde sa largeur et
                  la colonne Offre prend le reste. */}
              <table className="lk-entry-table of-table"
                     style={{ width: '100%', minWidth: largeurDe('name', 210) + VUES[vueOffres].cols.reduce((a, [id, , , w]) => a + largeurDe(id, w), 0) + 40 }}>
                <colgroup>
                  <col style={{ width: Object.keys(largeurs).length ? largeurDe('name', 210) : undefined }} />
                  {VUES[vueOffres].cols.map(([id, , , w]) => <col key={id} style={{ width: largeurDe(id, w) }} />)}
                  <col style={{ width: 40 }} />
                </colgroup>
                <thead>
                  <tr>
                    <Th col="name" label={p('Offre')} cls="of-lbl" sort={sort} onResize={(w) => poserLargeur('name', w)} />
                    {VUES[vueOffres].cols.map(([id, lbl, cls], k) => {
                      const f = figee(k + 1);
                      const classe = `${cls} ${f.className || ''}`.trim();
                      return <Th key={id} col={id} label={p(lbl)} cls={classe} sort={sort} style={f.style} onResize={(w) => poserLargeur(id, w)} />;
                    })}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {groupes.map((n) => (
                    <React.Fragment key={n.id}>
                      {n.label && <tr className="lk-grp-title"><td colSpan={VUES[vueOffres].cols.length + 2}><span className="lk-stick-txt">{p(n.label)}</span></td></tr>}
                      {n.items.map(ligne)}
                    </React.Fragment>
                  ))}
                  {canWrite && (
                    <tr className="lk-add-row"><td colSpan={VUES[vueOffres].cols.length + 2}><span className="lk-stick-txt"><button type="button" className="lk-add" onClick={onAdd}>＋ {p('Offre')}</button></span></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardShell>
    </>
  );
}
